/**
 * Odino turn model (ADR-0024 S2) — the structured shape of one assistant turn.
 *
 * The plan→act→observe loop streams events in order: prose tokens, then a tool
 * call, then its result, then more prose, … (across several agentic iterations).
 * Rendering them as one run-on text blob made the agent's work illegible (the
 * tool activity got glued into the prose). This reducer turns the SSE event
 * stream into an ordered list of **items** — interleaved text segments and
 * tool-activity chips — so the UI can show "🔧 get_catalog ✓" distinct from the
 * agent's words. Pure + unit-tested; the page just renders the items.
 */

export type ToolStatus = "running" | "ok" | "error";

/** A destructive-confirmation gate surfaced by a tool result (ADR-0024 B2): the
 *  rail renders it as a human confirm card with a button. */
export interface ConfirmPayload {
  message: string;
  items: string[];
}

export type TurnItem =
  | { kind: "text"; text: string }
  | { kind: "tool"; name: string; status: ToolStatus }
  | { kind: "confirm"; message: string; items: string[] };

/** The wire events the SSE stream emits (mirror of the backend orchestrator). */
export type OdinoWireEvent =
  | { type: "token"; text?: string }
  | { type: "thinking"; text?: string }
  | { type: "tool"; name: string }
  | {
      type: "tool_result";
      name?: string;
      is_error?: boolean;
      requires_confirmation?: boolean;
      confirm?: ConfirmPayload;
      /** When a structural write succeeds the orchestrator includes the admin
       *  URL of the changed area; the rail navigates there so the operator
       *  watches it land (ADR-0024 slice D). Not rendered — a side-effect. */
      navigate?: string;
    }
  | { type: "done"; text?: string; stop_reason?: string }
  | { type: "error"; message?: string }
  | { type: "cancelled" };

/**
 * Fold one wire event into the turn's items, returning a NEW array (never
 * mutates). `token` appends to the trailing text segment (or starts one);
 * `tool` pushes a running chip; `tool_result` resolves the most recent matching
 * running chip to ok/error. Terminal events (done/error/cancelled) don't change
 * the items — the page handles finalisation/error display.
 */
export function applyEvent(items: TurnItem[], ev: OdinoWireEvent): TurnItem[] {
  switch (ev.type) {
    case "token": {
      const delta = ev.text ?? "";
      if (!delta) return items;
      const last = items[items.length - 1];
      if (last && last.kind === "text") {
        return [...items.slice(0, -1), { kind: "text", text: last.text + delta }];
      }
      return [...items, { kind: "text", text: delta }];
    }
    case "tool":
      return [...items, { kind: "tool", name: ev.name, status: "running" }];
    case "tool_result": {
      const status: ToolStatus = ev.is_error ? "error" : "ok";
      let next = items;
      // Resolve the LAST still-running tool (optionally matching the name).
      for (let i = items.length - 1; i >= 0; i--) {
        const it = items[i];
        if (
          it.kind === "tool" &&
          it.status === "running" &&
          (!ev.name || it.name === ev.name)
        ) {
          next = items.slice();
          next[i] = { ...it, status };
          break;
        }
      }
      // A destructive gate adds a human confirm card (B2).
      if (ev.requires_confirmation && ev.confirm) {
        next = [...next, { kind: "confirm", message: ev.confirm.message, items: ev.confirm.items }];
      }
      return next;
    }
    default:
      return items;
  }
}

/**
 * Should the rail navigate the operator to `target` (ADR-0024 slice D)? Only
 * when it's a vetted in-app `/` path that's worth moving to. `target` is
 * whatever the orchestrator put on the `tool_result` event's `navigate` field (a
 * string only on a successful structural write). We DON'T navigate when:
 *  - it's the same path (redundant push), or
 *  - it's an ANCESTOR of where they already are (`/apps/x` while they're on
 *    `/apps/x/pages/y`). A multi-tool turn (create a page → edit it) emits
 *    an app-level url AND a page-level url; jumping "up" to the app and back into
 *    the page made the editor flicker (the bug the user hit). Staying put lets
 *    the later, more-specific page url win — a single, clean navigation.
 * Pure so the decision is unit-tested without the component.
 */
export function shouldNavigateTo(target: unknown, currentPath: string): target is string {
  if (typeof target !== "string" || !target.startsWith("/")) return false;
  // Odino operates only in the operator app (now at root). Never route into the
  // other realms — the portal (app-user) or the platform (superadmin) shells.
  if (target.startsWith("/portal/") || target.startsWith("/platform/")) return false;
  if (target === currentPath) return false;
  if (currentPath.startsWith(target + "/")) return false; // target is an ancestor — don't go up
  return true;
}

/** Convenience: the concatenated plain text of a turn (for tests / a11y). */
export function turnText(items: TurnItem[]): string {
  return items
    .filter((i): i is { kind: "text"; text: string } => i.kind === "text")
    .map((i) => i.text)
    .join("");
}
